import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Send, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TOPICS = [
  "Newcomer Essentials",
  "Budgeting",
  "Credit Building",
  "Mortgages",
  "Insurance",
  "Cost of Living",
  "Tools / calculators",
  "Correction or feedback",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* ── Header ── */}
      <section className="py-16 px-6 border-b border-[#E7E5E4] bg-white">
        <div className="max-w-screen-md mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-3">Contact</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1C1C] leading-tight tracking-tight">
            Question, correction, or story to share?
          </h1>
          <p className="text-[#57534E] text-lg mt-4 leading-relaxed max-w-xl">
            Real questions from real Canadian readers shape what gets written next. I read every message — and reply when I can.
          </p>
        </div>
      </section>

      {/* ── Form ── */}
      <section className="py-16 px-6">
        <div className="max-w-screen-md mx-auto">
          {status === "success" ? (
            <div
              data-testid="contact-success"
              className="bg-white border border-[#E7E5E4] rounded-xl p-12 text-center space-y-4 animate-fade-in"
            >
              <div className="flex justify-center">
                <div className="bg-[#F2EFE9] rounded-full p-4">
                  <CheckCircle className="text-[#1B4332]" size={32} />
                </div>
              </div>
              <h2 className="font-bold text-2xl text-[#1C1C1C]">Message received.</h2>
              <p className="text-[#57534E] text-base leading-relaxed max-w-sm mx-auto">
                I've got your message and will read it carefully. I reply when I can — usually within a few days.
              </p>
              <p className="text-[#57534E] text-sm">Check your inbox — a confirmation is on its way.</p>
              <button
                onClick={() => { setStatus(null); setForm({ name: "", email: "", topic: "", message: "" }); }}
                className="mt-4 text-[#1B4332] font-semibold text-sm hover:underline"
                data-testid="contact-send-another"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-[#E7E5E4] rounded-xl p-8 md:p-12 space-y-6"
              data-testid="contact-form"
            >
              {/* Name */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-[#1C1C1C]" htmlFor="name">
                  Your name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="First name is fine"
                  data-testid="contact-name"
                  className="w-full border border-[#E7E5E4] rounded-md px-4 py-3 text-[#1C1C1C] placeholder-[#A8A29E] focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332] transition-colors text-sm bg-[#F9F8F6]"
                />
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-[#1C1C1C]" htmlFor="email">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="you@example.com"
                  data-testid="contact-email"
                  className="w-full border border-[#E7E5E4] rounded-md px-4 py-3 text-[#1C1C1C] placeholder-[#A8A29E] focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332] transition-colors text-sm bg-[#F9F8F6]"
                />
              </div>

              {/* Topic */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-[#1C1C1C]" htmlFor="topic">
                  Topic <span className="text-[#A8A29E] font-normal">(optional)</span>
                </label>
                <select
                  id="topic"
                  name="topic"
                  value={form.topic}
                  onChange={handleChange}
                  data-testid="contact-topic"
                  className="w-full border border-[#E7E5E4] rounded-md px-4 py-3 text-[#1C1C1C] focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332] transition-colors text-sm bg-[#F9F8F6] appearance-none"
                >
                  <option value="">— Pick one if it helps —</option>
                  {TOPICS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div className="space-y-1.5">
                <label className="text-sm font-semibold text-[#1C1C1C]" htmlFor="message">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="What's on your mind?"
                  data-testid="contact-message"
                  className="w-full border border-[#E7E5E4] rounded-md px-4 py-3 text-[#1C1C1C] placeholder-[#A8A29E] focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332] transition-colors text-sm bg-[#F9F8F6] resize-none"
                />
              </div>

              {status === "error" && (
                <div
                  data-testid="contact-error"
                  className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-md px-4 py-3"
                >
                  <AlertCircle size={16} />
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="contact-submit-btn"
                className="w-full sm:w-auto bg-[#1B4332] text-white font-semibold px-8 py-3.5 rounded-md hover:bg-[#2D6A4F] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Send size={16} />
                {status === "loading" ? "Sending..." : "Send message"}
              </button>

              <p className="text-[#A8A29E] text-xs">No newsletter signup · No data shared</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
